import { useState } from 'react';
import type { ActiveIncident, IncidentDetail, StatusComponent } from '../api/statusApi';
import { fetchIncidentDetail } from '../api/statusApi';
import { formatDateTime, INCIDENT_STATUS_LABEL, SEVERITY_LABEL } from '../statusMeta';
import { IconChevronDown, IconClock } from './icons';
import './IncidentTimeline.css';

interface Props {
  activeIncidents: ActiveIncident[];
  /** Optional: recently resolved incidents to show after the active ones. */
  recentResolved?: ActiveIncident[];
  components: StatusComponent[];
}

function componentNames(ids: string[], components: StatusComponent[]): string {
  const byId = new Map(components.map((c) => [c.id, c.name]));
  return ids.map((id) => byId.get(id) ?? id).join(', ');
}

function IncidentCard({ incident, components }: { incident: ActiveIncident; components: StatusComponent[] }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isResolved = incident.status === 'resolved';

  async function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail && !loading) {
      setLoading(true);
      setError(false);
      try {
        const full = await fetchIncidentDetail(incident.id);
        setDetail(full);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  }

  const updates = detail?.updates ?? incident.updates;

  return (
    <li className={`incident-card${isResolved ? ' incident-card--resolved' : ''}`}>
      <button className="incident-card__header" onClick={() => void toggle()} aria-expanded={expanded}>
        <div className="incident-card__heading">
          <span className={`severity-badge severity-badge--${incident.severity}`}>
            {SEVERITY_LABEL[incident.severity]}
          </span>
          <span className="incident-card__title">{incident.title}</span>
        </div>
        <div className="incident-card__meta">
          <span className="incident-card__status">{INCIDENT_STATUS_LABEL[incident.status]}</span>
          <IconChevronDown className={`incident-card__chevron${expanded ? ' incident-card__chevron--open' : ''}`} />
        </div>
      </button>
      <p className="incident-card__components">{componentNames(incident.component_ids, components)}</p>
      {expanded && (
        <div className="incident-card__body">
          {loading && <p className="incident-card__loading">Loading full history…</p>}
          {error && <p className="incident-card__error">Couldn't load full history — showing what we have.</p>}
          <ol className="incident-updates">
            {[...updates].reverse().map((u, i) => (
              <li key={i} className="incident-update">
                <div className="incident-update__meta">
                  <IconClock width={14} height={14} />
                  <time>{formatDateTime(u.posted_at)}</time>
                  <span className="incident-update__status">{INCIDENT_STATUS_LABEL[u.status]}</span>
                </div>
                <p>{u.body}</p>
              </li>
            ))}
          </ol>
          {detail?.post_incident_report && (
            <div className="post-incident-report">
              <h4>Post-incident report</h4>
              <p>
                <strong>What happened:</strong> {detail.post_incident_report.what_happened}
              </p>
              <p>
                <strong>Impact window:</strong> {detail.post_incident_report.impact_window}
              </p>
              <p>
                <strong>Fix:</strong> {detail.post_incident_report.fix}
              </p>
              <p>
                <strong>Prevention:</strong> {detail.post_incident_report.prevention}
              </p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function IncidentTimeline({ activeIncidents, recentResolved = [], components }: Props) {
  const hasAny = activeIncidents.length > 0 || recentResolved.length > 0;

  return (
    <section className="incident-timeline card" aria-label="Incident timeline">
      <h2>Incidents</h2>
      {!hasAny && (
        <p className="incident-timeline__empty">No incidents reported. All systems have been running smoothly.</p>
      )}
      {activeIncidents.length > 0 && (
        <>
          <h3 className="incident-timeline__subheading">Active</h3>
          <ul className="incident-timeline__list">
            {activeIncidents.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} components={components} />
            ))}
          </ul>
        </>
      )}
      {recentResolved.length > 0 && (
        <>
          <h3 className="incident-timeline__subheading">Recently resolved</h3>
          <ul className="incident-timeline__list">
            {recentResolved.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} components={components} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
